import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  // Modal states
  modals: {
    connectWallet: false,
    networkSwitch: false,
    transactionDetails: null,
    contractDetails: null,
  },

  // Loading states
  loading: {
    deployment: false,
    transaction: false,
    balance: false,
  },

  // Form states
  forms: {
    deployment: {
      chain: "ethereum",
      tokenName: "VIITUM",
      tokenSymbol: "VIIT",
      initialSupply: "",
    },
    transfer: {
      contractAddress: "",
      toAddress: "",
      amount: "",
      chain: "ethereum",
    },
    balance: {
      contractAddress: "",
      walletAddress: "",
      chain: "ethereum",
    },
  },

  // Notifications/toasts
  notifications: [],

  // Selected items
  selected: {
    contract: null,
    transaction: null,
    chain: "ethereum",
  },

  // Filters
  filters: {
    contracts: {
      chain: "all",
      status: "all",
      search: "",
    },
    transactions: {
      chain: "all",
      type: "all",
      status: "all",
      search: "",
    },
  },

  // Pagination
  pagination: {
    contracts: {
      page: 1,
      limit: 10,
    },
    transactions: {
      page: 1,
      limit: 10,
    },
  },
};

const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    // Modal actions
    openModal: (state, action) => {
      const { modalName, data } = action.payload;
      state.modals[modalName] = data || true;
    },

    closeModal: (state, action) => {
      const modalName = action.payload;
      state.modals[modalName] = false;
    },

    closeAllModals: (state) => {
      Object.keys(state.modals).forEach((key) => {
        state.modals[key] = false;
      });
    },

    // Loading actions
    setLoading: (state, action) => {
      const { key, value } = action.payload;
      state.loading[key] = value;
    },

    // Form actions
    updateForm: (state, action) => {
      const { formName, field, value } = action.payload;
      if (state.forms[formName]) {
        state.forms[formName][field] = value;
      }
    },

    resetForm: (state, action) => {
      const formName = action.payload;
      if (initialState.forms[formName]) {
        state.forms[formName] = { ...initialState.forms[formName] };
      }
    },

    resetFormExceptChain: (state, action) => {
      const formName = action.payload;
      if (initialState.forms[formName] && state.forms[formName]) {
        const currentChain = state.forms[formName].chain;
        state.forms[formName] = {
          ...initialState.forms[formName],
          chain: currentChain,
        };
      }
    },

    setFormData: (state, action) => {
      const { formName, data } = action.payload;
      if (state.forms[formName]) {
        state.forms[formName] = { ...state.forms[formName], ...data };
      }
    },

    // Notification actions
    addNotification: (state, action) => {
      const notification = {
        id: Date.now() + Math.random(),
        timestamp: Date.now(),
        ...action.payload,
      };
      state.notifications.push(notification);
    },

    removeNotification: (state, action) => {
      const id = action.payload;
      state.notifications = state.notifications.filter((n) => n.id !== id);
    },

    clearNotifications: (state) => {
      state.notifications = [];
    },

    // Selection actions
    setSelected: (state, action) => {
      const { key, value } = action.payload;
      state.selected[key] = value;
    },

    clearSelected: (state, action) => {
      const key = action.payload;
      state.selected[key] = null;
    },

    // Filter actions
    setFilter: (state, action) => {
      const { category, key, value } = action.payload;
      if (state.filters[category]) {
        state.filters[category][key] = value;
      }
    },

    resetFilters: (state, action) => {
      const category = action.payload;
      if (initialState.filters[category]) {
        state.filters[category] = { ...initialState.filters[category] };
      }
    },

    // Pagination actions
    setPagination: (state, action) => {
      const { category, key, value } = action.payload;
      if (state.pagination[category]) {
        state.pagination[category][key] = value;
      }
    },

    resetPagination: (state, action) => {
      const category = action.payload;
      if (initialState.pagination[category]) {
        state.pagination[category] = { ...initialState.pagination[category] };
      }
    },
  },
});

// Action creators
export const {
  openModal,
  closeModal,
  closeAllModals,
  setLoading,
  updateForm,
  resetForm,
  resetFormExceptChain,
  setFormData,
  addNotification,
  removeNotification,
  clearNotifications,
  setSelected,
  clearSelected,
  setFilter,
  resetFilters,
  setPagination,
  resetPagination,
} = uiSlice.actions;

// Selectors
export const selectModals = (state) => state.ui.modals;
export const selectModal = (modalName) => (state) => state.ui.modals[modalName];
export const selectLoading = (state) => state.ui.loading;
export const selectLoadingState = (key) => (state) => state.ui.loading[key];
export const selectForms = (state) => state.ui.forms;
export const selectForm = (formName) => (state) => state.ui.forms[formName];
export const selectNotifications = (state) => state.ui.notifications;
export const selectSelected = (state) => state.ui.selected;
export const selectSelectedItem = (key) => (state) => state.ui.selected[key];
export const selectFilters = (state) => state.ui.filters;
export const selectFilter = (category) => (state) => state.ui.filters[category];
export const selectPagination = (state) => state.ui.pagination;
export const selectPaginationState = (category) => (state) =>
  state.ui.pagination[category];

export default uiSlice.reducer;
